import React from 'react';
import { Avatar, Box, Dialog, Tooltip, Typography, Button, Menu, MenuItem } from '@material-ui/core';
import DarkModeIcon from '@material-ui/icons/Brightness3';
import DashboardIcon from '@material-ui/icons/Dashboard';
import PowerSettingsNewOutlinedIcon from '@material-ui/icons/PowerSettingsNewOutlined';
import LightModeIcon from '@material-ui/icons/WbSunny';
import PersonIcon from '@material-ui/icons/Person';
import BusinessIcon from '@material-ui/icons/Business';
import ChromeReaderModeIcon from '@material-ui/icons/ChromeReaderMode';
import InfoIcon from '@material-ui/icons/Info';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import { ReactComponent as AzerbaijaniFlagIcon } from 'assets/icons/flags/az.svg';
import { ReactComponent as EnglishFlagIcon } from 'assets/icons/flags/en.svg';
import { ReactComponent as RussianFlagIcon } from 'assets/icons/flags/ru.svg';
import { ReactComponent as StandartBadge } from 'assets/bagdes/medal-2.svg';
import { ReactComponent as ProfessionalBadge } from 'assets/bagdes/medal-3.svg';
import { ReactComponent as PremiumBadge } from 'assets/bagdes/medal-4.svg';
import { ReactComponent as PrepaidBadge } from 'assets/bagdes/medal.svg';
import { useTranslation } from 'react-i18next';
import { useStyles } from './navigation-menu.style';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import { useNotifications } from 'context/NotificationsContext';
import { useCurrentPlanQuery } from 'graphql/generated';
import { defaultAvatar } from 'config';
import { AdminMenuItems, EmployerMenuItems, JobseekerMenuItems } from 'components/profile-menu/menu-items';
import { Link } from 'components';

export const NavagationMenu: React.FC<{ onCloseNavigationMenu }> = ({ onCloseNavigationMenu }) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const { isEmployer, isAdmin, isLoggedIn, openModal } = useAuth();
    const { isDarkMode, toggleTheme, language = 'az', setLanguage } = useSettings();
    const { data: pricingData } = useCurrentPlanQuery({ fetchPolicy: 'cache-and-network' });
    const currentPlan = pricingData?.currentPlan?.plan;

    const { notify } = useNotifications();
    const { user, removeAuthData } = useAuth();

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

    const handleClose = (): void => setAnchorEl(null);

    const badgeMap = {
        [1]: <PrepaidBadge className={classes.prepaid} />,
        [2]: <StandartBadge className={classes.standart} />,
        [3]: <PremiumBadge className={classes.premium} />,
        [4]: <ProfessionalBadge className={classes.professional} />,
    };

    const menuItems = [
        {
            title: 'jobseekerList',
            icon: <PersonIcon />,
            route: '/jobseekers',
        },
        {
            title: 'companyList',
            icon: <BusinessIcon />,
            route: '/companies',
        },
        {
            title: 'blog',
            icon: <ChromeReaderModeIcon />,
            route: '/blog',
        },
        {
            title: 'about',
            icon: <InfoIcon />,
            route: '/about',
        },
    ];

    const renderMenuItems = (): React.ReactElement => {
        if (isEmployer) {
            return <EmployerMenuItems />;
        }
        if (isAdmin) {
            return <AdminMenuItems />;
        }
        return <JobseekerMenuItems handleClose={onCloseNavigationMenu} />;
    };

    const logOut = (): void => {
        removeAuthData();
        onCloseNavigationMenu();

        notify({
            type: 'success',
            message: t('auth:logout'),
        });
    };

    const languages = {
        az: { name: 'Azərbaycanca', icon: AzerbaijaniFlagIcon },
        en: { name: 'English', icon: EnglishFlagIcon },
        ru: { name: 'Русский', icon: RussianFlagIcon },
    };

    const LanguageIcon = languages[language].icon;
    return (
        <Dialog fullScreen open={true} onClose={onCloseNavigationMenu} className={classes.root} style={{ zIndex: 100 }}>
            {!isLoggedIn && (
                <Box className={classes.userMenu}>
                    <Button
                        onClick={() => openModal()}
                        startIcon={
                            <Box className={classes.iconContainer}>
                                <ExitToAppIcon />
                            </Box>
                        }
                        className={classes.userMenuItem}
                    >
                        <Typography className={classes.userMenuItemText}>{t('header:login')}</Typography>
                    </Button>
                </Box>
            )}
            {isLoggedIn && (
                <>
                    <div className={classes.userDetails}>
                        <Link to={`/d/profile`}>
                            <Button fullWidth className={classes.profileButton}>
                                <div style={{ display: 'flex', flexDirection: 'row' }}>
                                    <Avatar className={classes.avatar} src={user?.avatar?.url || defaultAvatar} />
                                    <div className={classes.userInfo}>
                                        <Typography
                                            className={classes.userName}
                                        >{`${user?.firstName} ${user?.lastName}`}</Typography>
                                        <Typography variant="h6" color="textSecondary" className={classes.userRole}>
                                            {t(`roles:${user?.role}`)}
                                            {isEmployer && currentPlan && (
                                                <Tooltip title={currentPlan.name}>
                                                    {badgeMap[currentPlan.order]}
                                                </Tooltip>
                                            )}
                                        </Typography>
                                    </div>
                                </div>
                            </Button>
                        </Link>
                    </div>

                    <Box className={classes.userMenu}>
                        <Link to="/d/home">
                            <Button
                                startIcon={
                                    <Box className={classes.iconContainer}>
                                        <DashboardIcon />
                                    </Box>
                                }
                                className={classes.userMenuItem}
                            >
                                <Typography className={classes.userMenuItemText}>
                                    {t('mainProfileMenu:dashboard')}
                                </Typography>
                            </Button>
                        </Link>
                        {renderMenuItems()}
                    </Box>
                </>
            )}
            <Box className={classes.userMenu}>
                {menuItems.map((item, index) => (
                    <Link key={index} to={item.route}>
                        <Button
                            onClick={onCloseNavigationMenu}
                            startIcon={<Box className={classes.iconContainer}>{item.icon}</Box>}
                            className={classes.userMenuItem}
                        >
                            <Typography className={classes.userMenuItemText}>{t(`header:${item.title}`)}</Typography>
                        </Button>
                    </Link>
                ))}
            </Box>
            <Box className={classes.userMenu}>
                <div className={classes.languageWrapper}>
                    <Button className={classes.language} onClick={(e) => setAnchorEl(e.currentTarget)}>
                        <LanguageIcon width={28} height={28} style={{ marginRight: 6 }} />
                        {languages[language].name}
                    </Button>
                </div>
                <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
                    {Object.keys(languages).map((key) => (
                        <MenuItem
                            key={key}
                            value="en"
                            selected={language === key}
                            onClick={() => {
                                i18n.changeLanguage(key);
                                setLanguage(key);
                                handleClose();
                            }}
                        >
                            {languages[key].name}
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
            <Box className={classes.userMenu}>
                <Button
                    onClick={toggleTheme}
                    startIcon={
                        <Box className={classes.iconContainer}>
                            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon style={{ transform: 'rotate(45deg)' }} />}
                        </Box>
                    }
                    className={classes.userMenuItem}
                >
                    <Typography className={classes.userMenuItemText}>
                        {isDarkMode ? t('mainProfileMenu:lightTheme') : t('mainProfileMenu:darkTheme')}
                    </Typography>
                </Button>
                {isLoggedIn && (
                    <Button
                        onClick={(): void => logOut()}
                        startIcon={
                            <Box className={classes.iconContainer}>
                                <PowerSettingsNewOutlinedIcon />
                            </Box>
                        }
                        className={classes.userMenuItem}
                    >
                        <Typography className={classes.userMenuItemText}>{t('mainProfileMenu:logout')}</Typography>
                    </Button>
                )}
            </Box>
        </Dialog>
    );
};
